import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, ArticleDTO, NewsletterDTO } from "../api";
import { useAuth } from "../auth";
import Builder from "../builder/Builder";
import { Doc } from "../builder/types";
import { useAutosave, SaveStatus } from "../hooks";
import { printNewsletter } from "../print";

const statusLabel: Record<SaveStatus, string> = { idle: "", saving: "Saving…", saved: "Saved", error: "Save failed" };

export default function NewsletterEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();

  const [nl, setNl] = useState<NewsletterDTO | null>(null);
  const [title, setTitle] = useState("");
  const [doc, setDoc] = useState<Doc | null>(null);
  const [mode, setMode] = useState<"edit" | "read">("edit");
  const [theme, setTheme] = useState<"dark" | "light">(() => (localStorage.getItem("vox-theme") as any) || "dark");
  const [err, setErr] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [submitted, setSubmitted] = useState<ArticleDTO[]>([]);

  const { status, schedule } = useAutosave<{ title: string; head: any; blocks: any[] }>((p) => api.saveNewsletter(id!, p));

  useEffect(() => {
    if (!id) return;
    api
      .getNewsletter(id)
      .then((n) => {
        setNl(n);
        setTitle(n.title);
        setDoc({ head: n.head, blocks: n.blocks });
      })
      .catch((e) => setErr(e.message));
  }, [id]);

  const openPicker = async () => {
    setPickerOpen((o) => !o);
    if (!pickerOpen) {
      try {
        const all = await api.listArticles();
        setSubmitted(all.filter((a) => a.status === "submitted"));
      } catch {
        /* ignore */
      }
    }
  };

  const onDocChange = (next: Doc) => {
    setDoc(next);
    schedule({ title, head: next.head, blocks: next.blocks });
  };
  const onTitleChange = (t: string) => {
    setTitle(t);
    if (doc) schedule({ title: t, head: doc.head, blocks: doc.blocks });
  };

  const addArticle = async (a: ArticleDTO) => {
    if (!id) return;
    const res = await api.addArticleToNewsletter(id, a.id);
    setDoc({ head: res.newsletter.head, blocks: res.newsletter.blocks });
    setPickerOpen(false);
  };

  const togglePublish = async () => {
    if (!nl) return;
    const publish = nl.status !== "published";
    if (publish && !confirm("Publish this issue? It becomes visible to all viewers.")) return;
    const updated = await api.publishNewsletter(nl.id, publish);
    setNl(updated);
  };

  if (err)
    return (
      <div className="page-wrap" style={{ textAlign: "center" }}>
        <p style={{ color: "var(--accent)" }}>{err}</p>
        <Link to="/newsletters" className="btn">← Back</Link>
      </div>
    );
  if (!doc || !nl) return <div className="mono" style={{ padding: 60, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>Loading…</div>;

  return (
    <div>
      <div className="no-print" style={{ background: "var(--paper)", borderBottom: "1px solid var(--rule2)", position: "sticky", top: 43, zIndex: 20 }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", flexWrap: "wrap" }}>
          <button className="btn" onClick={() => navigate("/newsletters")}>← List</button>
          <input
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Issue title"
            style={{ flex: 1, minWidth: 160, background: "transparent", border: "1px solid var(--rule2)", color: "var(--ink)", font: "600 14px 'Zilla Slab',sans-serif", padding: "7px 10px", borderRadius: 2 }}
          />
          <span className="mono" style={{ fontSize: 10, color: status === "error" ? "var(--accent)" : "var(--muted)", minWidth: 56 }}>
            {statusLabel[status]}
          </span>
          {mode === "edit" && (
            <button className="btn" onClick={openPicker} style={pickerOpen ? { borderColor: "var(--accent)", color: "var(--accent)" } : undefined}>
              ＋ Add article
            </button>
          )}
          <button className="btn" onClick={() => setMode(mode === "edit" ? "read" : "edit")} style={mode === "edit" ? { borderColor: "var(--accent)", color: "var(--accent)" } : undefined}>
            {mode === "edit" ? "✓ Editing" : "✎ Edit"}
          </button>
          <button className="btn" onClick={() => { const t = theme === "dark" ? "light" : "dark"; setTheme(t); localStorage.setItem("vox-theme", t); }}>
            {theme === "dark" ? "Light" : "Dark"}
          </button>
          <button className="btn" onClick={() => printNewsletter(theme)}>⎙ Print</button>
          <span className="mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".1em", color: nl.status === "published" ? "var(--gold)" : "var(--muted)" }}>
            {nl.status}
          </span>
          {can("admin") && (
            <button className="btn btn-accent" onClick={togglePublish}>
              {nl.status === "published" ? "Unpublish" : "Publish ▸"}
            </button>
          )}
        </div>

        {pickerOpen && mode === "edit" && (
          <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px 14px" }}>
            <div style={{ border: "1px solid var(--rule2)", background: "var(--paper2)", padding: "12px 14px" }}>
              <div className="mono" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: ".12em", color: "var(--accent)", marginBottom: 10 }}>
                Submitted articles — click to append a snapshot
              </div>
              {submitted.length === 0 && <div style={{ color: "var(--muted)", fontSize: 13 }}>No submitted articles available.</div>}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 8 }}>
                {submitted.map((a) => (
                  <button
                    key={a.id}
                    className="addbtn"
                    style={{ display: "block" }}
                    onClick={() => addArticle(a)}
                    title={`${a.blocks.length} block(s)`}
                  >
                    <div style={{ fontFamily: "'Zilla Slab',sans-serif", fontSize: 14, color: "var(--ink)" }}>{a.title}</div>
                    <div className="mono" style={{ fontSize: 9.5, color: "var(--muted)", marginTop: 3 }}>
                      {a.authorName || a.authorSub} · {a.blocks.length} blocks
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "20px 0 60px" }}>
        <Builder doc={doc} variant="newsletter" mode={mode} theme={theme} onChange={onDocChange} onUploadImage={api.uploadImage} />
      </div>
    </div>
  );
}
